import React from 'react';
import { X, Upload, Clock, AlertCircle, CheckCircle } from 'lucide-react';

/**
 * UploadLimitModal - Shows upload quota status before allowing upload
 *
 * Shows one of two states:
 * 1. Uploads available: "You have X of 3 free uploads remaining today" + Proceed button
 * 2. Limit reached: "Daily limit reached. Try again in Xh Xm" + countdown
 */
const UploadLimitModal = ({
  isOpen,
  onClose,
  onProceed,
  limitStatus
}) => {
  if (!isOpen || !limitStatus) return null;

  const { allowed, remaining, limit, current, resetsAt, isExempt } = limitStatus;

  // Calculate time until reset
  const getTimeUntilReset = () => {
    if (!resetsAt) return null;

    const now = new Date();
    const resetTime = new Date(resetsAt);
    const diffMs = resetTime.getTime() - now.getTime();

    if (diffMs <= 0) return 'Soon';

    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes} minutes`;
  };

  const timeUntilReset = getTimeUntilReset();

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose}></div>

      <div className="relative z-50 min-h-screen flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm animate-in fade-in zoom-in duration-200">
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">Upload Status</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Exempt User (Demo) */}
            {isExempt && (
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-purple-100 flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Unlimited Uploads
                </h3>
                <p className="text-sm text-gray-600 mb-6">
                  You have unlimited uploads as a demo user.
                </p>
                <button
                  onClick={onProceed}
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 px-4 rounded-xl font-medium hover:from-purple-700 hover:to-blue-700 transition-all flex items-center justify-center gap-2"
                >
                  <Upload className="w-5 h-5" />
                  Continue to Upload
                </button>
              </div>
            )}

            {/* Uploads Available */}
            {!isExempt && allowed && (
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                  <Upload className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Ready to Upload
                </h3>

                {/* Quota Display */}
                <div className="mb-4">
                  <div className="flex justify-center gap-1 mb-2">
                    {[...Array(limit)].map((_, i) => (
                      <div
                        key={i}
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                          i < remaining
                            ? 'bg-green-500 text-white'
                            : 'bg-gray-200 text-gray-400'
                        }`}
                      >
                        {i < remaining ? '✓' : ''}
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-gray-600">
                    <span className="font-semibold text-green-600">{remaining}</span> of {limit} free uploads remaining today
                  </p>
                </div>

                <button
                  onClick={onProceed}
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 px-4 rounded-xl font-medium hover:from-purple-700 hover:to-blue-700 transition-all flex items-center justify-center gap-2"
                >
                  <Upload className="w-5 h-5" />
                  Continue to Upload
                </button>

                <p className="text-xs text-gray-500 mt-3">
                  Upload limit resets every 24 hours
                </p>
              </div>
            )}

            {/* Limit Reached */}
            {!isExempt && !allowed && (
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-orange-100 flex items-center justify-center">
                  <Clock className="w-8 h-8 text-orange-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Daily Limit Reached
                </h3>

                {/* Empty Quota Display */}
                <div className="mb-4">
                  <div className="flex justify-center gap-1 mb-2">
                    {[...Array(limit)].map((_, i) => (
                      <div
                        key={i}
                        className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium bg-gray-200 text-gray-400"
                      >

                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-gray-600">
                    <span className="font-semibold text-orange-600">0</span> of {limit} uploads remaining
                  </p>
                </div>

                {/* Countdown */}
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-4">
                  <div className="flex items-center justify-center gap-2 text-orange-700">
                    <Clock className="w-5 h-5" />
                    <span className="font-medium">
                      Try again in {timeUntilReset || 'a few hours'}
                    </span>
                  </div>
                  <p className="text-xs text-orange-600 mt-2">
                    Your upload limit resets automatically
                  </p>
                </div>

                <button
                  onClick={onClose}
                  className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-xl font-medium hover:bg-gray-200 transition-all"
                >
                  Got It
                </button>

                <p className="text-xs text-gray-500 mt-3">
                  Free users can upload 3 documents per day
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadLimitModal;
